import React, { useEffect, useState } from 'react';
import styles from './ScreeningOverviewContent.module.css';
import { classNames } from '../../../utils';
import { ListingCard } from '../../../organisms/listing-card';
import SkeletonComponent from '../../../../components/SkeletonComponent';
import ScreeningNavigation from '../../../organisms/screening-navigation/ScreeningNavigation';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../../enums';
import screeningSteps, { Action } from '../../../organisms/screening-navigation/screeningSteps';
import Text from '../../../atoms/typography/Text';
import { useTranslation } from 'react-i18next';
import findOverview, { EmploymentIncomeVerificationSource, FreelanceOrBusinessOwnershipIncomeVerificationSource, ScreeningOverview } from '../../../../api/screening/findOverview';
import { ScreeningOverviewCompleteStep, ScreeningOverviewIncompleteStep } from '../../../molecules/screening-overview-steps';
import { useRoutePath } from '../../../utils/useRoutePath';
import { useParams, useSearchParams } from 'react-router-dom';
import { ScreeningOverviewHouseholdCompositionStepTable, ScreeningOverviewAdditionalInformationStepTable } from '../../../molecules/screening-overview-step-table';
import Form from '../../../molecules/form/Form';
import Notifications from '../../../organisms/notifications/Notifications';
import moment from 'moment';
import ScreeningOverviewIncomeVerificationStepTable from '../../../molecules/screening-overview-step-table/ScreeningOverviewIncomeVerificationStepTable';
import ScreeningOverviewEmploymentIncomeVerificationSourceStepTable from '../../../molecules/screening-overview-step-table/ScreeningOverviewEmploymentIncomeVerificationSourceStepTable';
import IncomeVerificationIncomeSourceTypeEnum from '../../../enums/screening/IncomeVerificationIncomeSourceTypeEnum';
import ScreeningOverviewFreelanceOrBusinessOwnershipIncomeVerificationSourceStepTable from '../../../molecules/screening-overview-step-table/ScreeningOverviewFreelanceOrBusinessOwnershipIncomeVerificationSourceStepTable';
import PageContentNotFound from '../../../organisms/page-content-not-found/PageContentNotFound';

const ScreeningOverviewContent = () => {
    const { t } = useTranslation();
    const routePath = useRoutePath();

    const { homeseekerId } = useParams();
    const [ searchParams ] = useSearchParams();

    const authorisationId = searchParams.get('authorisationId');

    const [ isFetchingScreeningOverview, setIsFetchingScreeningOverview ] = useState(true);
    const [ screeningOverview, setScreeningOverview ] = useState<ScreeningOverview>(null);
    const [ isSubmittingOverview, setIsSubmittingOverview ] = useState<boolean>(false);
    const [ isSubmittingOverviewSuccess, setIsSubmittingOverviewSuccess ] = useState<boolean>(false);
    const [ error, setError ] = useState<boolean>(!authorisationId);

    useEffect(() => {
        if (error) {
            setIsFetchingScreeningOverview(false);
            return;
        }

        fetchOverview();
        setSubmitButtonTranslation();
    }, []);

    const fetchOverview = async () => {
        try {
            const response = await findOverview(homeseekerId, authorisationId);
            setScreeningOverview(response);
        } catch (error) {
            setError(true);
        } finally {
            setIsFetchingScreeningOverview(false);
        }
    }

    const setSubmitButtonTranslation = () => {
        const submitButton = screeningSteps.ScreeningOverview.buttons.find(button => button.action === 'submit');

        if (submitButton !== undefined) {
            if (authorisationId) {
                submitButton.translationKey = 'screening.overview.button.submit.access'
            } else {
                submitButton.translationKey = 'screening.overview.button.submit.save'
            }
        }
    }

    const renderListingCard = () => {
        if (!authorisationId) {
            return null;
        }

        if (isFetchingScreeningOverview) {
            return (
                <div className={styles.listingCard}>
                    <SkeletonComponent height={300} width={360}/>
                </div>
            );
        }

        if (!screeningOverview || !screeningOverview.listing) {
            return null;
        }

        return (
            <div className={styles.listingCard}>
                <ListingCard
                    listing={screeningOverview.listing}
                    agency={screeningOverview.agency}
                />
            </div>
        );
    }

    const renderIncompleteStepsSection = () => {
        if (isFetchingScreeningOverview) {
            return (
                <div className={styles.gridLg}>
                    <SkeletonComponent height={92}/>
                    <SkeletonComponent height={92}/>
                </div>
            );
        }

        if (!screeningOverview) {
            return null;
        }

        const stepsLength = Object.keys(screeningOverview.incompleteSteps).length;

        if (stepsLength === 0) {
            return null;
        }

        return (
            <div className={styles.gridLg}>
                <Text
                    color={ColorEnum.Gray800}
                    size={FontSizeEnum.Lg}
                    weight={FontWeightEnum.Semibold}
                >{t('screening.overview.incompleteSteps.title', { steps: stepsLength })}</Text>
                <hr/>
                {screeningOverview.incompleteSteps.householdComposition && (
                    <ScreeningOverviewIncompleteStep
                        title={t(`screening.overview.step.title.household_composition`)}
                        routePath={routePath('ScreeningHousehold', { verificationRequestAuthorisationId: authorisationId })}
                        required={screeningOverview.incompleteSteps.householdComposition.required}
                        alerts={screeningOverview.incompleteSteps.householdComposition.alerts}
                    />
                )}
                {screeningOverview.incompleteSteps.idVerification && (
                    <ScreeningOverviewIncompleteStep
                        title={t(`screening.overview.step.title.id_verification`)}
                        routePath={routePath('ScreeningIdVerificationIntroduction', { homeseekerId }, { authorisationId })}
                        required={screeningOverview.incompleteSteps.idVerification.required}
                        alerts={screeningOverview.incompleteSteps.idVerification.alerts}
                    />
                )}
                {screeningOverview.incompleteSteps.incomeVerificationSources && (
                    <ScreeningOverviewIncompleteStep
                        title={t(`screening.overview.step.title.income_verification_sources`)}
                        routePath={routePath('ScreeningIncomeVerificationOverview', { homeseekerId }, { authorisationId })}
                        required={screeningOverview.incompleteSteps.incomeVerificationSources.required}
                        alerts={screeningOverview.incompleteSteps.incomeVerificationSources.alerts}
                    />
                )}
                {screeningOverview.incompleteSteps.incomeVerification && (
                    <ScreeningOverviewIncompleteStep
                        title={t(`screening.overview.step.title.income_verification`)}
                        routePath={routePath('ScreeningIncomeVerificationProvideTransactions', { homeseekerId }, { authorisationId })}
                        required={screeningOverview.incompleteSteps.incomeVerification.required}
                        alerts={screeningOverview.incompleteSteps.incomeVerification.alerts}
                    />
                )}
                {screeningOverview.incompleteSteps.additionalInformation && (
                    <ScreeningOverviewIncompleteStep
                        title={t(`screening.overview.step.title.additional_information`)}
                        routePath={routePath('ScreeningAdditionalInformation', { homeseekerId }, { authorisationId })}
                        required={screeningOverview.incompleteSteps.additionalInformation.required}
                        alerts={screeningOverview.incompleteSteps.additionalInformation.alerts}
                    />
                )}
            </div>
        );
    }

    const renderCompleteStepsSection = () => {
        if (isFetchingScreeningOverview) {
            return (
                <div className={styles.gridLg}>
                    <SkeletonComponent height={92}/>
                    <SkeletonComponent height={92}/>
                </div>
            );
        }

        if (!screeningOverview) {
            return null;
        }

        const stepsLength = Object.keys(screeningOverview.completeSteps).length;

        if (stepsLength === 0) {
            return null;
        }

        return (
            <div className={styles.gridLg}>
                <Text
                    color={ColorEnum.Gray800}
                    size={FontSizeEnum.Lg}
                    weight={FontWeightEnum.Semibold}
                >{t('screening.overview.completeSteps.title')}</Text>
                <hr/>
                {screeningOverview.completeSteps.householdComposition && (
                    <ScreeningOverviewCompleteStep
                        title={t(`screening.overview.step.title.household_composition`)}
                        routePath={routePath('ScreeningHousehold', { verificationRequestAuthorisationId: authorisationId })}
                        updatedAt={moment(screeningOverview.completeSteps.householdComposition.updatedAt)}
                        required={screeningOverview.completeSteps.householdComposition.required}
                    >
                        <ScreeningOverviewHouseholdCompositionStepTable data={screeningOverview.completeSteps.householdComposition.data}/>
                    </ScreeningOverviewCompleteStep>
                )}
                {screeningOverview.completeSteps.idVerification && (
                    <ScreeningOverviewCompleteStep
                        title={t(`screening.overview.step.title.id_verification`)}
                        routePath={routePath('ScreeningIdVerificationIntroduction', { homeseekerId }, { authorisationId })}
                        updatedAt={moment(screeningOverview.completeSteps.idVerification.updatedAt)}
                        required={screeningOverview.completeSteps.idVerification.required}
                    />
                )}
                {screeningOverview.completeSteps.incomeVerificationSources && (
                    <ScreeningOverviewCompleteStep
                        title={t(`screening.overview.step.title.income_verification_sources`)}
                        routePath={routePath('ScreeningIncomeVerificationOverview', { homeseekerId }, { authorisationId })}
                        updatedAt={moment(screeningOverview.completeSteps.incomeVerificationSources.updatedAt)}
                        required={screeningOverview.completeSteps.incomeVerificationSources.required}
                    >
                        {screeningOverview.completeSteps.incomeVerificationSources.data
                            .filter(source => Object.values(IncomeVerificationIncomeSourceTypeEnum).includes(source.type as IncomeVerificationIncomeSourceTypeEnum))
                            .map((source, index) => {
                                switch (source.type) {
                                    case IncomeVerificationIncomeSourceTypeEnum.Employment:
                                        return (
                                            <ScreeningOverviewEmploymentIncomeVerificationSourceStepTable
                                                key={index}
                                                data={source as EmploymentIncomeVerificationSource}
                                            />
                                        );
                                    case  IncomeVerificationIncomeSourceTypeEnum.Freelance:
                                    case  IncomeVerificationIncomeSourceTypeEnum.BusinessOwnership:
                                        return (
                                            <ScreeningOverviewFreelanceOrBusinessOwnershipIncomeVerificationSourceStepTable
                                                key={index}
                                                data={source as FreelanceOrBusinessOwnershipIncomeVerificationSource}
                                            />
                                        );
                                }
                                return null;
                            })
                        }
                    </ScreeningOverviewCompleteStep>
                )}
                {screeningOverview.completeSteps.incomeVerification && (
                    <ScreeningOverviewCompleteStep
                        title={t(`screening.overview.step.title.income_verification`)}
                        routePath={routePath('ScreeningIncomeVerificationProvideTransactions', { homeseekerId }, { authorisationId })}
                        updatedAt={moment(screeningOverview.completeSteps.incomeVerification.updatedAt)}
                        required={screeningOverview.completeSteps.incomeVerification.required}
                    >
                        {screeningOverview.completeSteps.incomeVerification.data.bankStatements && (
                            <ScreeningOverviewIncomeVerificationStepTable data={screeningOverview.completeSteps.incomeVerification.data}/>
                        )}
                    </ScreeningOverviewCompleteStep>
                )}
                {screeningOverview.completeSteps.additionalInformation && (
                    <ScreeningOverviewCompleteStep
                        title={t(`screening.overview.step.title.additional_information`)}
                        routePath={routePath('ScreeningAdditionalInformation', { homeseekerId }, { authorisationId })}
                        required={screeningOverview.completeSteps.additionalInformation.required}
                        updatedAt={moment(screeningOverview.completeSteps.additionalInformation.updatedAt)}
                    >
                        <ScreeningOverviewAdditionalInformationStepTable data={screeningOverview.completeSteps.additionalInformation.data}/>
                    </ScreeningOverviewCompleteStep>
                )}
            </div>
        )
    }

    const handleClick = (action: Action) => {
        if (action === 'skip') {
            // TODO: Implement authorisation flow
        }
    }

    const handleSubmit = async () => {
        setIsSubmittingOverview(true);
        setIsSubmittingOverviewSuccess(false);

        try {
            if (authorisationId) {
                // TODO: Implement authorisation flow
            } else {
                // TODO: Implement authorisation flow
            }
        } catch (error) {
            Notifications.add({
                type: 'failed',
                message: t('api.request.failed')
            });
        } finally {
            setIsSubmittingOverview(false);
            setIsSubmittingOverviewSuccess(false);
        }
    }

    if (error) {
        return <PageContentNotFound/>;
    }

    return (
        <Form
            onSubmit={handleSubmit}
        >
            <div className={styles.screeningOverviewContent}>
                <div className={classNames(styles.screeningOverviewContainer, !authorisationId && styles.noListingCard)}>
                    <div className={styles.headerContainer}>
                        <Text
                            color={ColorEnum.Primary700}
                            weight={FontWeightEnum.Bold}
                            size={FontSizeEnum.DisplayXs}
                        >{t(screeningSteps.ScreeningOverview.pageTitleTranslationKey)}</Text>
                    </div>
                    {renderListingCard()}
                    <div className={styles.gridLg}>
                        <Text
                            color={ColorEnum.Gray800}
                            size={FontSizeEnum.Md}
                        >{t('screening.overview.description')}</Text>
                        {renderIncompleteStepsSection()}
                        {renderCompleteStepsSection()}
                        <ScreeningNavigation
                            isSubmitting={isSubmittingOverview}
                            isSuccessful={isSubmittingOverviewSuccess}
                            onClick={handleClick}
                        />
                    </div>
                </div>
            </div>
        </Form>
    );
}

export default ScreeningOverviewContent;
